import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Company, type Person, type PreviewRow, type Rule }
  from '@/api/client'
import { Banner, Empty, ErrorNote, Spinner, Tag, fmtDate, todayISO } from '@/components/ui'
import { CompanyPicker } from '@/components/CompanyPicker'
import { useAuth } from '@/lib/auth'
import {
  DAYS_OF_MONTH, EVERY_UNIT, FREQ, INTERVALS, MONTH, MONTHS, NEEDS, NWD,
  WEEKDAYS, describe, inCsv, retune, toggleCsv,
} from '@/lib/recurrence'


/** When the day's recurring tasks are generated.
 *
 *  `recurring_generate_time` was always editable, but only as one raw text box in an
 *  alphabetical list of twenty on Master data - with no time picker, and nothing
 *  saying which of three `*_time` keys makes the standing work appear. Undiscoverable
 *  is indistinguishable from fixed, which is how it came to be reported as hardcoded
 *  at 06:00. Same argument, and the same shape, as `WhenTheDayEnds` on Day close.
 *
 *  Paired with the master switch for the same reason that one pairs the end time with
 *  auto-close: a time set while generation is off gives half of what was asked for,
 *  and nothing on the screen would say so.
 *
 *  Superuser only, because it decides when everything happens. The API enforces that;
 *  this only avoids showing a control that would refuse.
 */
function WhenTheseAreCreated({ digestTime }: { digestTime: string }) {
  const qc = useQueryClient()
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, string>>('/settings'),
  })
  const [draft, setDraft] = useState<{ at: string; on: boolean } | null>(null)
  const [err, setErr] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: (v: { at: string; on: boolean }) =>
      api.patch('/settings', { recurring_generate_time: v.at,
                               recurring_enabled: String(v.on) }),
    onSuccess: () => {
      setErr(null); setDraft(null); setSaved(true)
      // The backend re-registers the jobs on save, so there is no restart to prompt.
      qc.invalidateQueries({ queryKey: ['settings'] })
      qc.invalidateQueries({ queryKey: ['rules'] })
    },
    onError: setErr,
  })

  if (settings.isLoading) return null
  const live = {
    at: (settings.data?.recurring_generate_time ?? '').slice(0, 5) || '06:00',
    on: (settings.data?.recurring_enabled ?? 'true').toLowerCase() !== 'false',
  }
  const form = draft ?? live
  const dirty = form.at !== live.at || form.on !== live.on
  // The one combination that quietly does the wrong thing: the digest goes out
  // before the day's standing work exists, so the list it carries is incomplete.
  const afterDigest = !!digestTime && form.at >= digestTime

  return (
    <div className="card mt-4">
      <div className="card-head">
        <b className="text-[13px]">When these are created</b>
        <span className="flex-1" />
        {saved && !dirty ? <Tag>saved · jobs rescheduled</Tag> : null}
      </div>
      <div className="p-4 space-y-3">
        {err ? <ErrorNote error={err} /> : null}
        <div className="flex gap-4 items-start">
          <div className="w-40">
            <label className="label">Time</label>
            <input className="field" type="time" value={form.at}
                   onChange={(e) => {
                     setSaved(false)
                     setDraft({ ...form, at: e.target.value })
                   }} />
          </div>
          <div className="flex-1 space-y-1.5 pt-1">
            {[[true, 'Create the day\'s recurring tasks automatically'],
              [false, 'Leave it to me — I will use the button above']].map(([v, label]) => (
              <label key={String(v)} className="flex items-center gap-2 text-[12.5px]">
                <input type="radio" checked={form.on === v}
                       onChange={() => { setSaved(false); setDraft({ ...form, on: v as boolean }) }} />
                {label}
              </label>
            ))}
          </div>
        </div>

        <p className="text-[11.8px] text-ink-3 leading-relaxed">
          {form.on
            ? <>At <b>{form.at}</b> each working day, every rule due that day becomes a
                real task. If the app is down at {form.at}, a catch-up creates them as
                soon as it is back.</>
            : <>Nothing is created automatically. Rules stay listed and stay due, and
                the only thing that files them is the button above.</>}
        </p>

        {form.on && afterDigest && (
          <p className="text-[11.8px] text-pend-ink bg-pend-tint rounded px-2.5 py-1.5">
            That is at or after the morning digest ({digestTime}), so the digest goes
            out before the day's recurring tasks exist — everybody gets a list that is
            missing their standing work. Set this earlier than {digestTime}.
          </p>
        )}

        <div className="flex gap-2">
          <button className="btn btn-primary" disabled={!dirty || save.isPending}
                  onClick={() => save.mutate(form)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {dirty && (
            <button className="btn" onClick={() => { setDraft(null); setErr(null) }}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function Recurring() {
  const qc = useQueryClient()
  const { me } = useAuth()
  // Shared cache key with Master data and the card below, so a save on any of them
  // refreshes the others.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, string>>('/settings'),
  })
  const generateAt =
    (settings.data?.recurring_generate_time ?? '').slice(0, 5) || '06:00'
  const digestAt = (settings.data?.digest_time ?? '').slice(0, 5) || '09:30'
  const autoOn =
    (settings.data?.recurring_enabled ?? 'true').toLowerCase() !== 'false'
  const [creating, setCreating] = useState(false)
  // The rule open in the editor, or null. Separate from `creating` so the drawer can
  // tell which of the two it is without a sentinel.
  const [editing, setEditing] = useState<Rule | null>(null)
  const [err, setErr] = useState<unknown>(null)
  const rules = useQuery({ queryKey: ['rules'], queryFn: () => api.get<Rule[]>('/recurrences') })

  const toggle = useMutation({
    mutationFn: (r: Rule) =>
      api.post(`/recurrences/${r.recurrence_id}/${r.active ? 'deactivate' : 'activate'}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rules'] }),
    onError: setErr,
  })
  // Deliberately NOT the main mechanism. The 06:00 job creates these every working
  // day, and a catch-up job covers the morning the app was down at 06:00. This is
  // only for the case that neither can help with: a rule added later in the day,
  // whose first occurrence the Owner wants now rather than tomorrow.
  const [made, setMade] = useState<number | null>(null)
  const generate = useMutation({
    mutationFn: () => api.post<{ counts?: { created?: number } }>(
      '/recurrences/generate?dry_run=false'),
    onSuccess: (out) => {
      setMade(out?.counts?.created ?? 0)
      qc.invalidateQueries({ queryKey: ['rules'] })
      qc.invalidateQueries({ queryKey: ['tasks'] })
    },
    onError: setErr,
  })

  if (rules.isLoading) return <Spinner label="Loading rules" />
  const rows = rules.data ?? []
  const invalid = rows.filter((r) => !r.valid)

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      {invalid.length > 0 && (
        <Banner tone="warn">
          <b>{invalid.length} rule(s) cannot run</b> — most often a missing
          Non-Working Day Rule, which has no default on purpose: the system will not
          guess whether a deadline moves earlier or later.
        </Banner>
      )}
      <div className="flex flex-wrap items-center gap-2.5 mb-2">
        <span className="mono text-xs text-ink-3">
          {rows.length} rule(s) · {rows.filter((r) => r.active).length} active
        </span>
        <span className="hidden sm:block flex-1" />
        <button className="btn btn-sm" disabled={generate.isPending}
                onClick={() => generate.mutate()}>
          {generate.isPending ? 'Generating…' : 'Generate due tasks now'}
        </button>
        <button className="btn btn-sm btn-primary" onClick={() => setCreating(true)}>
          + New rule
        </button>
      </div>

      {/* What the button is for. It runs the same generation the timed job runs, so
          without this it reads as though nothing happens automatically - which is
          exactly the question it prompted.

          The time is read from the setting rather than written here. It said "at
          06:00" in prose while the real value lived in `recurring_generate_time`, so
          an Owner who changed it got a page that told them the wrong thing. */}
      <p className="text-[11.8px] text-ink-3 mb-4">
        {autoOn ? (
          <>These are created{' '}
          <b className="text-ink-2">automatically at {generateAt}</b> each working
          day, before the morning digest — and if the app is down at {generateAt}, a
          catch-up creates them as soon as it is back. Use{' '}</>
        ) : (
          <>Automatic creation is <b className="text-ink-2">off</b>, so nothing is
          filed unless you file it. Use{' '}</>
        )}
        <b className="text-ink-2">Generate due tasks now</b> only when you have added
        a rule today and want its first task before tomorrow morning.
        {made !== null && (
          <span className="block mt-1 text-ink-2">
            {made === 0
              ? 'Last run: nothing was due that had not already been created.'
              : `Last run: ${made} task${made === 1 ? '' : 's'} created.`}
          </span>
        )}
      </p>

      {rows.length === 0 ? (
        <Empty title="No recurring rules yet"
               hint="Standing work — GST filings, TDS returns, PF challans — can generate itself each period."
               action={<button className="btn btn-primary" onClick={() => setCreating(true)}>Create the first rule</button>} />
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="w-full border-collapse">
              <thead><tr>
                <th className="th w-14">Ref</th><th className="th">Task</th>
                <th className="th w-44">Company</th><th className="th w-40">Repeats</th>
                <th className="th w-24">Non-working</th><th className="th w-32">Responsible</th>
                <th className="th w-20">Made</th><th className="th w-24">Active</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.recurrence_id} className={r.valid ? '' : 'bg-rej-tint/40'}>
                    <td className="td mono text-primary">{r.ref}</td>
                    <td className="td">
                      {r.task}
                      {!r.valid && (
                        <span className="block text-[11.5px] text-rej-ink mt-1">
                          {r.problems[0]}
                        </span>
                      )}
                    </td>
                    <td className="td text-[12.5px]">{r.company}</td>
                    <td className="td text-[12.5px]">{describe(r)}</td>
                    <td className="td"><Tag>{r.non_working_day_rule || '—'}</Tag></td>
                    <td className="td text-[12.5px]">{r.responsible_person}</td>
                    <td className="td mono text-xs">{r.generated_count}</td>
                    <td className="td">
                      <button className="btn btn-sm mr-1.5"
                              onClick={() => setEditing(r)}>
                        Edit
                      </button>
                      <button className="btn btn-sm" disabled={toggle.isPending}
                              onClick={() => toggle.mutate(r)}>
                        {r.active ? 'Pause' : 'Resume'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-rule text-[11.8px] text-ink-3">
            Pausing a rule stops future generation and leaves everything already
            generated alone — those are real tasks now.
          </div>
        </div>
      )}

      {/* Below the rules, like Day close puts its own control below what closing
          does: the list is what the page is for, and this is the thing that decides
          when the list turns into work. */}
      {me?.is_superuser && <WhenTheseAreCreated digestTime={digestAt} />}

      {creating && <RuleEditor onClose={() => setCreating(false)}
                               onSaved={() => { setCreating(false); qc.invalidateQueries({ queryKey: ['rules'] }) }} />}
      {editing && <RuleEditor rule={editing} onClose={() => setEditing(null)}
                              onSaved={() => {
                                setEditing(null)
                                qc.invalidateQueries({ queryKey: ['rules'] })
                              }} />}
    </>
  )
}

/** The live next-five-dates preview is the affordance that makes a recurrence rule
 *  verifiable without waiting a month. The endpoint is pure date maths. */
function RuleEditor({ rule, onClose, onSaved }: {
  rule?: Rule; onClose: () => void; onSaved: () => void
}) {
  // One component for both, because the fields, the validation and the live preview
  // are the same question either way - and a second editor is how the two drift.
  const [form, setForm] = useState({
    task: rule?.task ?? '',
    company: rule?.company ?? '',
    responsible_person: rule?.responsible_person ?? '',
    approver: rule?.approver ?? '',
    priority: rule?.priority ?? 'Regular',
    frequency: rule?.frequency ?? 'MONTHLY',
    interval: rule?.interval ?? 1,
    days_of_week: rule?.days_of_week ?? '',
    day_of_month: rule?.day_of_month ?? '20',
    months_of_year: rule?.months_of_year ?? '',
    non_working_day_rule: rule?.non_working_day_rule ?? '',
    start_date: rule?.start_date || todayISO(),
    end_date: rule?.end_date ?? '',
    remarks: rule?.remarks ?? '',
    active: rule?.active === false ? 'No' : 'Yes',
  })
  const [err, setErr] = useState<unknown>(null)
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }))

  const companies = useQuery({
    queryKey: ['companies'], queryFn: () => api.get<Company[]>('/companies'),
  })
  const people = useQuery({
    queryKey: ['users'], queryFn: () => api.get<Person[]>('/users'),
  })

  // Everything the server would refuse, checked here so Save is not a way of finding
  // out. It used to omit the schedule fields, so a WEEKLY rule with no days picked
  // looked ready and came back 422 - and the live preview showed the same error
  // underneath, which reads as the screen being broken rather than incomplete.
  const needs = NEEDS[form.frequency] ?? NEEDS.MONTHLY
  const ready = !!(form.task && form.company && form.responsible_person
    && form.non_working_day_rule
    && (!needs.days || form.days_of_week)
    && (!needs.dayOfMonth || form.day_of_month)
    && (!needs.months || form.months_of_year))
  const preview = useQuery({
    queryKey: ['preview', form],
    queryFn: () => api.post<PreviewRow[]>('/recurrences/preview',
                                        { ...payload(), count: 5 }),
    enabled: !!ready,
    retry: false,
  })
  // `end_date: ''` is not a date, and the API types it as an optional one - so a
  // blank field has to travel as null rather than as an empty string, which is what
  // tells the server to clear it.
  const payload = () => ({ ...form, end_date: form.end_date || null })

  const save = useMutation({
    mutationFn: () => rule
      ? api.patch(`/recurrences/${rule.recurrence_id}`, payload())
      : api.post('/recurrences', payload()),
    onSuccess: onSaved, onError: setErr,
  })

  return (
    <>
      <div className="fixed inset-0 bg-ink/10 z-10" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-[560px] bg-surface border-l border-rule
                        shadow-2xl z-20 flex flex-col">
        <div className="px-5 py-3.5 border-b border-rule flex items-center">
          <h3 className="text-base font-semibold tracking-tight">
            {rule ? `Edit ${rule.ref}` : 'New recurring rule'}
          </h3>
          <span className="flex-1" />
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-lg px-1">×</button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-3.5">
          {err ? <ErrorNote error={err} /> : null}
          <div>
            <label className="label">Task</label>
            <input className="field" value={form.task} onChange={(e) => set('task', e.target.value)}
                   placeholder="File GSTR-3B for {{month_year}}" />
            <p className="text-[11.5px] text-ink-3 mt-1">
              Tokens: <code>{'{{month_year}}'}</code> <code>{'{{month}}'}</code>{' '}
              <code>{'{{quarter}}'}</code> <code>{'{{fy}}'}</code>{' '}
              <code>{'{{due_date}}'}</code> — without one, every instance reads identically.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Company</label>
              {/* The same searchable picker the entry grid and the drawer use. A
                  native select made a firm with forty clients scroll for one. */}
              <CompanyPicker
                companies={(companies.data ?? []).filter((c) => c.active)}
                value={form.company ? [form.company] : []}
                onChange={(ns) => set('company', ns[0] ?? '')}
                onDone={() => undefined} />
            </div>
            <div>
              <label className="label">Responsible</label>
              <select className="field" value={form.responsible_person}
                      onChange={(e) => set('responsible_person', e.target.value)}>
                <option value="">Choose…</option>
                {(people.data ?? []).map((p) => <option key={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Approver (optional)</label>
              <select className="field" value={form.approver} onChange={(e) => set('approver', e.target.value)}>
                <option value="">No approval needed</option>
                {(people.data ?? []).filter((p) => p.roles.includes('APPROVER') || p.is_owner).map((p) => (
                  <option key={p.name}>{p.name}</option>))}
              </select>
            </div>
            <div>
              <label className="label">Priority</label>
              <select className="field" value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                {['Urgent', 'Important', 'Regular'].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Only the fields this frequency uses - see `NEEDS`, which mirrors what
              the server validates. Changing the frequency retunes them rather than
              leaving the last one's answers behind. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label" htmlFor="rule-freq">Repeats</label>
              <select id="rule-freq" className="field" value={form.frequency}
                      onChange={(e) => setForm((f) => retune(f, e.target.value))}>
                {FREQ.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
            {NEEDS[form.frequency]?.interval && (
            <div>
              <label className="label" htmlFor="rule-interval">Every</label>
              {/* There was no interval field at all, so "every 2 weeks" could not be
                  written from this screen - though the list already describes it and
                  the server has always stored it. A list rather than a number box:
                  beyond a year is not a recurrence anybody writes. */}
              <div className="flex items-center gap-2">
                <select id="rule-interval" className="field w-20"
                        value={form.interval}
                        onChange={(e) => set('interval', Number(e.target.value))}>
                  {INTERVALS.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-[12.5px] text-ink-3">
                  {EVERY_UNIT[form.frequency] ?? 'period'}
                  {Number(form.interval) > 1 ? 's' : ''}
                </span>
              </div>
            </div>
            )}
            {NEEDS[form.frequency]?.dayOfMonth && (
              <div>
                <label className="label" htmlFor="rule-dom">Day of month *</label>
                <select id="rule-dom" className="field" value={form.day_of_month}
                        onChange={(e) => set('day_of_month', e.target.value)}>
                  {DAYS_OF_MONTH.map((d) => (
                    <option key={d} value={d}>
                      {d === 'LAST' ? 'Last day of the month' : d}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Multi-select, as buttons rather than a list you scroll: seven or twelve
              options that read at a glance, and the shape of the answer is visible
              without opening anything.

              These were text boxes - "MON,THU" and "1,4,7,10" - which asks somebody
              to know a format and gets it wrong in ways the server can only refuse
              afterwards. "'MONDAY' is not a weekday" is a real message it produced.
              The values still travel as CSV, because that is what the API takes;
              nobody types them any more. */}
          {NEEDS[form.frequency]?.days && (
            <div>
              <label className="label">Days of week *</label>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => {
                  const on = inCsv(form.days_of_week, d)
                  return (
                    <button key={d} type="button" aria-pressed={on}
                            onClick={() => set('days_of_week',
                                               toggleCsv(form.days_of_week, d, WEEKDAYS))}
                            className={`px-2.5 h-8 rounded-md border text-[12.5px] ${
                              on ? 'bg-primary text-primary-on border-primary font-medium'
                                 : 'bg-surface border-rule text-ink-2 hover:text-ink'}`}>
                      {d}
                    </button>
                  )
                })}
              </div>
              {!form.days_of_week && (
                <p className="text-[11.5px] text-rej-ink mt-1">Pick at least one day.</p>
              )}
            </div>
          )}
          {NEEDS[form.frequency]?.months && (
            <div>
              <label className="label">Months *</label>
              <div className="flex flex-wrap gap-1.5">
                {MONTHS.map((m) => {
                  const on = inCsv(form.months_of_year, m)
                  return (
                    <button key={m} type="button" aria-pressed={on}
                            onClick={() => set('months_of_year',
                                               toggleCsv(form.months_of_year, m, MONTHS))}
                            className={`px-2.5 h-8 rounded-md border text-[12.5px] ${
                              on ? 'bg-primary text-primary-on border-primary font-medium'
                                 : 'bg-surface border-rule text-ink-2 hover:text-ink'}`}>
                      {MONTH[m]}
                    </button>
                  )
                })}
              </div>
              {!form.months_of_year && (
                <p className="text-[11.5px] text-rej-ink mt-1">
                  Pick at least one month.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="label">If it lands on a non-working day *</label>
            <div className="flex gap-2">
              {NWD.map((n) => (
                <button key={n} type="button"
                        onClick={() => set('non_working_day_rule', n)}
                        className={`btn flex-1 justify-center ${form.non_working_day_rule === n ? 'btn-primary' : ''}`}>
                  {n === 'BEFORE' ? 'Move earlier' : n === 'AFTER' ? 'Move later' : 'Skip it'}
                </button>
              ))}
            </div>
            <p className="text-[11.5px] text-ink-3 mt-1">
              Required, with no default — the system will not guess whether a
              deadline should move earlier or later. Filing early never costs
              anything; filing late does.
            </p>
          </div>

          <div>
            <label className="label">Starts</label>
            <input type="date" className="field w-48" value={form.start_date}
                   onChange={(e) => set('start_date', e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="rule-end">Ends (optional)</label>
            {/* The domain has enforced this since it was written - an end before the
                start is refused, and the occurrence walk bounds on it - but there was
                no field, so no rule could ever have one. A rule that should stop at
                the year end had to be paused by hand and remembered. */}
            <input id="rule-end" type="date" className="field w-48"
                   value={form.end_date} min={form.start_date}
                   onChange={(e) => set('end_date', e.target.value)} />
            <p className="text-[11.5px] text-ink-3 mt-1">
              {form.end_date
                ? <>Nothing is generated after {fmtDate(form.end_date)}. Tasks already
                    created are unaffected.</>
                : <>Leave blank to repeat indefinitely.</>}
            </p>
          </div>

          <div className="card">
            <div className="card-head"><b className="text-[13px]">Next five dates</b></div>
            {!ready ? (
              <p className="px-4 py-4 text-[12.5px] text-ink-3">
                Fill in the task, company, responsible person and the non-working-day
                rule to see when this would fire.
              </p>
            ) : preview.isLoading ? <Spinner label="Working it out" />
              : preview.error ? <div className="p-3"><ErrorNote error={preview.error} /></div>
              : (
              <div className="table-wrap">
                <table className="w-full">
                  <tbody>
                    {(preview.data ?? []).map((r, i) => (
                      <tr key={i} className="border-b border-rule-2 last:border-0">
                        <td className="td mono text-xs w-28">{fmtDate(r.occurrence)}</td>
                        <td className="td text-xs">
                          {r.skipped ? <Tag tone="bad">skipped</Tag>
                            : r.moved ? <>→ <b>{fmtDate(r.resolved)}</b> <Tag>moved</Tag></>
                            : <span className="text-ink-3">on the day</span>}
                        </td>
                        <td className="td text-[12.5px]">{r.task}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-rule p-4 flex gap-2">
          <button className="btn btn-primary" disabled={!ready || save.isPending}
                  onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : rule ? 'Save changes' : 'Create rule'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </aside>
    </>
  )
}
